import type { Subscription } from "@rails/actioncable";
import type * as ActionCable from "actioncable";
import consultationCorrectionsApi from "@/vue/api/consultation_corrections.js";
import type { CorrectionsPollingConnectionHandlers } from "@/vue/composables/useConsultationCorrectionsPolling";
import type {
  ConsultationExtractedQuestionItem,
  ConsultationPreprocessingRequestState,
} from "@/vue/stores/modules/consultation_preprocessing_request";
import * as mutationTypes from "../mutation-types";
import { runStaticCheckRules } from "@/vue/features/anytime-consultation-static-check";
import type { StaticCheckFinding } from "@/vue/features/anytime-consultation-static-check";

// =================================
// CorrectionRequestの基本型
// =================================

export type CorrectionType = "typo" | "misreading" | "unansweredQuestion";

export const CORRECTION_TYPES: CorrectionType[] = [
  "typo",
  "misreading",
  "unansweredQuestion",
];

export const TYPE_LABELS: Record<CorrectionType, string> = {
  typo: "誤字脱字",
  misreading: "相談文の再確認",
  unansweredQuestion: "疑問への回答不足",
};

// Requestのサーバー側のステータス
// "requested" は厳密にはサーバー側のステータスではないが、ここでまとめて扱う
export type LlmServerStatus = "notRequested" | "requested" | "started" | "completed" | "failed";

// 全Request（添削メニュー全体）のステータス。
export type LlmServerStatuses = Record<CorrectionType, LlmServerStatus>;

export const initialLlmServerStatuses = (): LlmServerStatuses => ({
  typo: "notRequested",
  misreading: "notRequested",
  unansweredQuestion: "notRequested",
});

export type ThinkingProgressMap = Record<CorrectionType, string>;

export const initialThinkingProgress = (): ThinkingProgressMap => ({
  typo: "",
  misreading: "",
  unansweredQuestion: "",
});

// =================================
// CorrectionRequestのフロント側での対応状況
// =================================

// CorrectionStatus: フロント側の状態管理も含めたRequest１つのステータス
// 1. notRequested
// 1-1. 未実行: notRequested
// 2. inProgress（フロント側では requested, started の両方を 処理中 として描画する）
// 2-1. 実行要求済み。まだサーバーからの応答はない: requested
// 2-2. サーバーが処理を開始した: started
// 3. completed（サーバー側での処理が完了したケースを、フロントでは以下の３つに分類する）
// 3-1. サーバー側の処理が完了し、未対応のSuggestionあり。要対応: suggestionPending
// 3-2. サーバー側の処理が完了し、すべてのSuggestionが対応済み(newが一つもない): suggestionResolved
// 3-3. サーバー側の処理が完了し、Suggestionが空だった(すべてのステータスのsuggestionが一つもない): noSuggestion
// 4. failed
// 4-1. サーバー側でエラーが発生した、または通信エラーなどで処理が完了しなかった: failed

export type CorrectionStatus = "notRequested" | "requested" | "started" | "suggestionPending" | "suggestionResolved" | "noSuggestion" | "failed";

interface CorrectionStatusesByTypeArgs {
  statuses: LlmServerStatuses;
  suggestions: Suggestion[];
  resolvedSuggestions: SuggestionWithResolution[];
}

export const correctionStatusesByType = ({
  statuses,
  suggestions,
  resolvedSuggestions,
}: CorrectionStatusesByTypeArgs): Record<CorrectionType, CorrectionStatus> => {
  const statusesByType = {} as Record<CorrectionType, CorrectionStatus>;

  CORRECTION_TYPES.forEach((type) => {
    const serverStatus = statuses[type] ?? "notRequested";

    if (serverStatus === "failed") {
      statusesByType[type] = "failed";
      return;
    }

    if (serverStatus === "requested" || serverStatus === "started") {
      statusesByType[type] = serverStatus;
      return;
    }

    if (serverStatus === "completed") {
      const hasSuggestions = suggestions.some((suggestion) => suggestion.correctionType === type);

      if (!hasSuggestions) {
        statusesByType[type] = "noSuggestion";
        return;
      }

      const hasNewSuggestion = resolvedSuggestions.some(
        (suggestion) => suggestion.correctionType === type && suggestion.resolution === "new"
      );

      statusesByType[type] = hasNewSuggestion ? "suggestionPending" : "suggestionResolved";
      return;
    }

    statusesByType[type] = "notRequested";
  });

  return statusesByType;
};

const isInProgressStatus = (status?: CorrectionStatus): boolean =>
  status === "requested" || status === "started";


// =================================
// Suggestionの基本型。
// バックエンドと構造は一致する
// =================================

export interface BaseSuggestion {
  id: number;
  correctionType: CorrectionType;
  quote?: string | null;
  replacement?: string | null;
  reason?: string | null;
  consultationExtractedQuestionId?: number | null;
  accepted: boolean;
  ignored: boolean;
}

export interface TypoSuggestion extends BaseSuggestion {
  correctionType: "typo";
  quote: string;
  replacement: string;
}

export interface MisreadingSuggestion extends BaseSuggestion {
  correctionType: "misreading";
  quote: string;
  reason: string;
}

export interface UnansweredQuestionSuggestion extends BaseSuggestion {
  correctionType: "unansweredQuestion";
  reason: string;
  consultationExtractedQuestionId: number;
}

export type Suggestion =
  | TypoSuggestion
  | MisreadingSuggestion
  | UnansweredQuestionSuggestion;

// =================================
// Suggestionのフロント側での対応状況
// - 回答文データなどを元に算出する
// =================================

// "new": 未対応
// "changed": すでに本文中にない。手で修正済みと推定
// "error": 本文中に２回以上に存在する(duplicated)のケースなど。表示しない。
// "closed"; accepted or ignoredなど、すでに対応完了が確定しているもの。表示しない。
export type SuggestionResolution =
  | "new"
  | "changed"
  | "error" // duplicated
  | "closed"; // accepted or ignored

export type SuggestionWithResolution = Suggestion & {
  resolution: SuggestionResolution;
  order: number | null;
  extractedQuestion?: ConsultationExtractedQuestionItem | null;
};

export interface SuggestionWithResolutionContext {
  suggestion: Suggestion;
  answerContent: string;
  extractedQuestion?: ConsultationExtractedQuestionItem | null;
}

export interface SuggestionWithResolutionsContext {
  suggestions: Suggestion[];
  answerContent: string;
  extractedQuestionsMap?: Record<number, ConsultationExtractedQuestionItem>;
}

const countOccurrences = (content: string, needle: string): number => {
  if (!needle) return 0;
  return content.split(needle).length - 1;
};

interface ResolutionArgs {
  suggestion: Suggestion;
  hasQuote: boolean;
  occurrenceCount: number | null;
}

const deriveResolution = ({ suggestion, hasQuote, occurrenceCount }: ResolutionArgs): SuggestionResolution => {
  if (suggestion.accepted || suggestion.ignored) return "closed";
  if (!hasQuote || occurrenceCount === null) return "new";
  if (occurrenceCount >= 2) return "error";
  if (occurrenceCount === 0) return "changed";
  return "new";
};

const RESOLUTION_ORDER_OFFSET = 1_000_000;

const deriveOrder = ({
  resolution,
  suggestionId,
}: {
  resolution: SuggestionResolution;
  suggestionId: number;
}): number | null => {
  const resolutionWeight: Record<SuggestionResolution, number | null> = {
    new: 0,
    changed: 1,
    error: null,
    closed: null,
  };
  const weight = resolutionWeight[resolution];
  if (weight === null) return null;
  return weight * RESOLUTION_ORDER_OFFSET + suggestionId;
};

const isCountableResolution = (
  resolution: SuggestionResolution
): resolution is Extract<SuggestionResolution, "new" | "changed"> => resolution === "new" || resolution === "changed";

export interface SuggestionResolutionCounts {
  new: number;
  changed: number;
}

export const countSuggestionResolutions = (
  suggestions: SuggestionWithResolution[]
): SuggestionResolutionCounts =>
  suggestions.reduce(
    (acc, suggestion) => {
      if (isCountableResolution(suggestion.resolution)) {
        acc[suggestion.resolution] += 1;
      }
      return acc;
    },
    { new: 0, changed: 0 }
  );

export const buildSuggestionWithResolution = ({
  suggestion,
  answerContent,
  extractedQuestion,
}: SuggestionWithResolutionContext): SuggestionWithResolution => {
  const normalizedQuote = suggestion.quote?.trim() ?? "";
  const hasQuote = normalizedQuote.length > 0;
  const occurrenceCount = hasQuote ? countOccurrences(answerContent, normalizedQuote) : null;
  const resolution = deriveResolution({ suggestion, hasQuote, occurrenceCount });
  const order = deriveOrder({ resolution, suggestionId: suggestion.id });

  return {
    ...suggestion,
    resolution,
    order,
    extractedQuestion: extractedQuestion ?? null,
  };
};

export const buildSuggestionsWithResolution = ({
  suggestions,
  answerContent,
  extractedQuestionsMap,
}: SuggestionWithResolutionsContext): SuggestionWithResolution[] => {
  const questionsMap = extractedQuestionsMap ?? {};

  return suggestions
    .map((suggestion) => {
      const extractedQuestion =
        suggestion.consultationExtractedQuestionId != null
          ? questionsMap[suggestion.consultationExtractedQuestionId] ?? null
          : null;

      return buildSuggestionWithResolution({ suggestion, answerContent, extractedQuestion });
    })
    .filter((suggestion) => suggestion.order !== null) // errorやclosedのsuggestionはorderがnullになるため、除外される
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

// =================================
// Api Clientの定義
// =================================
export interface SuggestionCommandPayload {
  suggestionId: number;
}

export interface ConsultationCorrectionSuggestionsClient {
  accept(payload: SuggestionCommandPayload): Promise<Suggestion>;
  ignore(payload: SuggestionCommandPayload): Promise<Suggestion>;
}

const apiClient: ConsultationCorrectionSuggestionsClient = {
  accept: ({ suggestionId }) => consultationCorrectionsApi.acceptSuggestion({ suggestionId }),
  ignore: ({ suggestionId }) => consultationCorrectionsApi.ignoreSuggestion({ suggestionId }),
};

// =================================
// accept / ignore 関係
// =================================
export interface AcceptParams {
  suggestionId: number;
  client?: ConsultationCorrectionSuggestionsClient;
}

export interface IgnoreParams {
  suggestionId: number;
  client?: ConsultationCorrectionSuggestionsClient;
}

type SuggestionCommand = SuggestionCommandPayload | number | undefined;

const extractSuggestionId = (payload: SuggestionCommand): number | undefined => {
  if (payload == null) return undefined;
  return typeof payload === "number" ? payload : payload.suggestionId;
};

const isTypoSuggestion = (suggestion: Suggestion): suggestion is TypoSuggestion => {
  return suggestion.correctionType === "typo";
};

const replaceFirstOccurrence = (content: string, beforeText: string, afterText: string): string => {
  if (!beforeText) return content;
  // String.replace()を使用すると、置換対象が正規表現として解釈されてしまう可能性があるため、indexOfとsliceで実装する
  const index = content.indexOf(beforeText);
  if (index === -1) return content;
  return `${content.slice(0, index)}${afterText}${content.slice(index + beforeText.length)}`;
};

const replaceAllOccurrences = (content: string, beforeText: string, afterText: string): string => {
  if (!beforeText) return content;
  // staticCheckのacceptでは、同一quoteが複数箇所ある場合に個別ではなく一括で置換する仕様。
  // split/join で beforeText をリテラルとして扱い、正規表現解釈を避ける。
  return content.split(beforeText).join(afterText);
};

const acceptSuggestion = async (
  { commit, state, rootState }: any,
  suggestionId: number
) => {
  const suggestion = state.suggestions.find((item: Suggestion) => item.id === suggestionId);
  if (!suggestion) return;
  if (!isTypoSuggestion(suggestion)) {
    throw new Error("typo以外のサジェストはacceptできません");
  }

  const updatedSuggestion = await apiClient.accept({ suggestionId });

  const answer = rootState.anytimeConsultations?.anytimeConsultationAnswer;
  if (answer) {
    const content = replaceFirstOccurrence(answer.content ?? "", suggestion.quote, suggestion.replacement);
    commit(mutationTypes.SET_ANYTIME_CONSULTATION_ANSWER_CONTENT, { content });
  }

  commit(mutationTypes.UPSERT_ANYTIME_CONSULTATION_CORRECTIONS_SUGGESTION, updatedSuggestion);
};

const ignoreSuggestion = async (
  { commit, state }: any,
  suggestionId: number
) => {
  const suggestion = state.suggestions.find((item: Suggestion) => item.id === suggestionId);
  if (!suggestion) return;
  const updatedSuggestion = await apiClient.ignore({ suggestionId });
  commit(mutationTypes.UPSERT_ANYTIME_CONSULTATION_CORRECTIONS_SUGGESTION, updatedSuggestion);
};

// =================================
// 添削の実行(ActionCableチャネル)
// =================================
// -------------
// サーバーからイベントを受け取る
// -------------
interface StartedPayload {
  event: "started";
  correctionType: CorrectionType;
}
interface CompletedPayload {
  event: "completed";
  correctionType: CorrectionType;
}
interface FailedPayload {
  event: "failed";
  correctionType: CorrectionType;
}
interface SuggestionPayload {
  event: "suggest";
  correctionType: CorrectionType;
  suggestion: Suggestion;
}
interface ThinkingProgressPayload {
  event: "thinking_progress";
  correctionType: CorrectionType;
  thinking: string;
}
// type StatusPayload = StartedPayload | CompletedPayload | FailedPayload;
type EventPayload = StartedPayload | CompletedPayload | FailedPayload | SuggestionPayload | ThinkingProgressPayload;

interface ChannelCallbacksParams {
  commit: (mutation: string, payload?: unknown) => void;
  dispatch: (action: string, payload?: unknown) => unknown;
  storeState: AnytimeConsultationCorrectionsState;
  consultationId: number;
  connectionHandlers?: CorrectionsPollingConnectionHandlers | null;
}

const channelCallbacks = ({
  commit,
  dispatch,
  storeState,
  consultationId,
  connectionHandlers,
}: ChannelCallbacksParams) => {
  return {
    connected: () => {
      const isReconnect = storeState.hasConnectedOnce;
      commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_CORRECTIONS_HAS_CONNECTED_ONCE, true);
      connectionHandlers?.handleChannelConnected({ consultationId, isReconnect });
      if (isReconnect) {
        console.warn("AnytimeConsultationChannel reconnected. Syncing state via REST.");
        dispatch("fetchCorrectionsData", { consultationId });
      } else {
        console.log("AnytimeConsultationChannel connected (initial).");
      }
    },
    received: (payload: unknown) => {
      const eventPayload = payload as EventPayload;
      // received内部でstateを更新するのは、thinking_progressの場合のみ。
      // それ以外のイベントの場合は、あくまでサーバー側で状態が変化したタイミング通知としてのみ受け取り、stateの更新はfetchCorrectionsDataに任せる。
      if (eventPayload.event === "suggest") {
        console.log("AnytimeConsultationChannel received suggestion", eventPayload);
      } else if (eventPayload.event === "thinking_progress") {
        commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_CORRECTIONS_THINKING_PROGRESS, {
          correctionType: eventPayload.correctionType,
          thinking: eventPayload.thinking,
        });
        return;
      } else { // StatusPayloadの時
        console.log("AnytimeConsultationChannel received status update", eventPayload);
      }

      connectionHandlers?.handleChannelReceived({ consultationId });
      dispatch("fetchCorrectionsData", { consultationId });
    },
    disconnected: () => {
      connectionHandlers?.handleChannelDisconnected({ consultationId });
    },
    rejected: () => {
      connectionHandlers?.handleChannelRejected({ consultationId });
    },
  };
};

interface SubscribeChannelParams {
  cable: ActionCable.Cable;
  consultationId: number;
  correctionTypes: CorrectionType[];
  answerContent: string;
  commit: (mutation: string, payload?: unknown) => void;
  dispatch: (action: string, payload?: unknown) => unknown;
  storeState: AnytimeConsultationCorrectionsState;
  connectionHandlers?: CorrectionsPollingConnectionHandlers | null;
}

const subscribeAnytimeConsultationChannel = ({
  cable,
  consultationId,
  correctionTypes,
  answerContent,
  commit,
  dispatch,
  storeState,
  connectionHandlers,
}: SubscribeChannelParams): Subscription => {
  console.log("Subscribing AnytimeConsultationChannel");

  return cable.subscriptions.create(
    {
      channel: "AnytimeConsultationChannel",
      id: consultationId,
      correction_types: correctionTypes,
      answer: answerContent,
    },
    channelCallbacks({
      commit,
      dispatch,
      storeState,
      consultationId,
      connectionHandlers,
    })
  );
};

// -------------
// 添削実行の実装
// -------------
export interface RequestCorrectionsParams {
  consultationId: number;
  cable: ActionCable.Cable;
  correctionTypes?: CorrectionType[];
  connectionHandlers?: CorrectionsPollingConnectionHandlers | null;
}

export interface CorrectionsPollingParams {
  consultationId: number;
}

const selectHasExtractedQuestions = (rootState: RootStateWithAnswer): boolean => {
  const preprocessingState = rootState.consultationPreprocessingRequest;
  if (!preprocessingState || preprocessingState.isComplete !== true) return false;
  const questions = preprocessingState.questions ?? [];
  return questions.length > 0;
};

const filterAvailableCorrectionTypes = (
  types: CorrectionType[],
  rootState: RootStateWithAnswer
): CorrectionType[] => {
  if (selectHasExtractedQuestions(rootState)) {
    return types;
  }
  return types.filter((type) => type !== "unansweredQuestion");
};

const requestCorrectionsImpl = async (
  { commit, state, rootState, dispatch }: any,
  { consultationId, cable, correctionTypes, connectionHandlers }: RequestCorrectionsParams
) => {
  const requestedTypes = correctionTypes && correctionTypes.length > 0 ? correctionTypes : CORRECTION_TYPES;
  const normalizedTypes = filterAvailableCorrectionTypes(requestedTypes, rootState);
  if (normalizedTypes.length === 0) {
    console.warn("[AnytimeConsultationCorrections] 有効な添削種別がありません");
    return;
  }

  await dispatch("unsubscribeAnytimeConsultationChannel");
  commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_CORRECTIONS_SELECTED_SUGGESTION_ID, undefined);

  const statuses = { ...state.statuses };
  normalizedTypes.forEach((type) => {
    statuses[type] = "requested";
  });

  commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_CORRECTIONS_STATUSES, statuses);
  commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_CORRECTIONS_SUGGESTIONS, []);
  commit(mutationTypes.CLEAR_ANYTIME_CONSULTATION_CORRECTIONS_THINKING_PROGRESS, normalizedTypes);

  const answerContent = rootState.anytimeConsultations?.anytimeConsultationAnswer?.content ?? "";

  const channel = subscribeAnytimeConsultationChannel({
    cable,
    consultationId,
    correctionTypes: normalizedTypes,
    answerContent,
    commit,
    dispatch,
    storeState: state as AnytimeConsultationCorrectionsState,
    connectionHandlers,
  });

  commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_CORRECTIONS_CHANNEL, channel);

  try {
    await consultationCorrectionsApi.requestCorrections({
      consultationId,
      correctionTypes: normalizedTypes,
      answer: answerContent,
    });
  } catch (error) {
    const failedStatuses = normalizedTypes.reduce((acc, type) => {
      acc[type] = "failed";
      return acc;
    }, {} as Partial<Record<CorrectionType, LlmServerStatus>>);

    commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_CORRECTIONS_STATUSES, failedStatuses);
    commit(mutationTypes.CLEAR_ANYTIME_CONSULTATION_CORRECTIONS_THINKING_PROGRESS, normalizedTypes);

    try {
      await dispatch("unsubscribeAnytimeConsultationChannel");
    } catch (unsubscribeError) {
      console.warn("Failed to unsubscribe channel after requestCorrections error", unsubscribeError);
    }

    throw error;
  }
};

// =================================
// サーバーから受け取った情報の正規化
// =================================

// ConsultationCorrectionRequestsController#index のレスポンスペイロード
// 過去の添削状態をまとめて受け取る
interface CorrectionRequestsResponse {
  statuses: Partial<Record<CorrectionType, LlmServerStatus>>;
  suggestions: Suggestion[];
}

const mergeStatuses = (
  statuses: Partial<Record<CorrectionType, LlmServerStatus>>
): LlmServerStatuses => ({
  ...initialLlmServerStatuses(),
  ...(statuses ?? {}),
});

const fetchInitialCorrectionsData = async (
  consultationId: number
): Promise<{ statuses: LlmServerStatuses; suggestions: Suggestion[] }> => {
  const response: CorrectionRequestsResponse | undefined = await consultationCorrectionsApi.fetchCorrectionRequests({ consultationId });

  if (response === undefined) {
    return {
      statuses: initialLlmServerStatuses(),
      suggestions: [],
    };
  }

  return {
    statuses: mergeStatuses(response.statuses),
    suggestions: response.suggestions,
  };
};

// statusEntriesの型
export interface StatusEntry {
  type: CorrectionType | "staticCheck";
  label: string;
  status: CorrectionStatus | "noQuestions";
  thinking: string;
  disabled: boolean;
}

// 添削メニューごとのステータス整理
function getThinkingProgressMap(thinkingProgress: ThinkingProgressMap | null | undefined): Record<CorrectionType, string> {
  if (!thinkingProgress) {
    return {
      typo: "",
      misreading: "",
      unansweredQuestion: "",
    };
  }
  return {
    typo: thinkingProgress.typo,
    misreading: thinkingProgress.misreading,
    unansweredQuestion: thinkingProgress.unansweredQuestion,
  };
}

function getStatusMap(correctionStatuses: Record<CorrectionType, CorrectionStatus> | null | undefined): Record<CorrectionType, CorrectionStatus> {
  if (!correctionStatuses) {
    return {
      typo: "notRequested",
      misreading: "notRequested",
      unansweredQuestion: "notRequested",
    };
  }
  return {
    typo: correctionStatuses.typo,
    misreading: correctionStatuses.misreading,
    unansweredQuestion: correctionStatuses.unansweredQuestion,
  };
}

// statusEntries
// AnytimeConsultationCorrectionTab.vue の 添削メニュー一覧で表示する内容を算出する。
// 基本的には、他のデータをまとめあげるだけだが、以下のケースのみ特殊処理があるので注意
// - まだ疑問の抽出が終わっていない(hasExtractedQuestions: false)場合、unansweredQuestion（疑問への回答不足）は、noQuestions（疑問点がないため添削対象外）ステータスになる。
export function getStatusEntries(
  correctionStatuses: Record<CorrectionType, CorrectionStatus> | null | undefined,
  thinkingProgress: ThinkingProgressMap | null | undefined,
  hasExtractedQuestions: boolean
): StatusEntry[] {
  const statusMap: Record<CorrectionType, CorrectionStatus> = getStatusMap(correctionStatuses);
  const thinkingMap: Record<CorrectionType, string> = getThinkingProgressMap(thinkingProgress);
  return CORRECTION_TYPES.map((type: CorrectionType): StatusEntry => {
    const disableUnanswered: boolean = type === "unansweredQuestion" && !hasExtractedQuestions;
    return {
      type,
      label: TYPE_LABELS[type],
      status: disableUnanswered ? "noQuestions" : statusMap[type],
      thinking: disableUnanswered ? "" : thinkingMap[type],
      disabled: disableUnanswered,
    };
  });
}
// =================================
// Storeの定義
// =================================

// -------------
// 型定義
// -------------

// actions context型
export interface StoreContext {
  state: AnytimeConsultationCorrectionsState;
  getters: AnytimeConsultationCorrectionsGetterContext;
  commit: (mutation: string, payload?: unknown) => void;
  dispatch: (action: string, payload?: unknown) => unknown;
  rootState: unknown;
  rootGetters: unknown;
}
export interface AnytimeConsultationCorrectionsState {
  statuses: LlmServerStatuses;
  suggestions: Suggestion[];
  selectedSuggestionId: number | undefined;
  autoCorrectionChannel: Subscription | null;
  hasConnectedOnce: boolean;
  thinkingProgress: ThinkingProgressMap;
  ignoredStaticCheckQuotes: string[]; // NOTE: staticCheck の ignore は findingのIDではなく quoteとして保持する。例えば "セルフケア" を1箇所ignoreした場合、本文中で登場する他の"セルフケア"もまとめて警告非表示にする（同一文言はそのままで良いことが多いため）。
  everTriggeredStaticCheckRules: {
    doubleQuote: boolean;
    brackets: boolean;
    all: boolean;
  };
}

export interface AnytimeConsultationCorrectionsGetterValues {
  suggestionsWithResolution: SuggestionWithResolution[];
  newSuggestions: SuggestionWithResolution[];
  newSuggestionsWithAnswerQuote: SuggestionWithResolution[];
  groupedStatusCounts: SuggestionResolutionCounts;
  correctionStatusesByType: Record<CorrectionType, CorrectionStatus>;
  selectedSuggestionId: number | undefined;
  selectedUnansweredQuestionId: number | undefined;
  isAllCorrectionsResolved: boolean;
  hasNotRequestedCorrections: boolean;
  hasInProgressCorrections: boolean;
  hasFailedCorrections: boolean;
  statusEntries: StatusEntry[];
  hasExtractedQuestions?: boolean;
  staticCheckFindings: StaticCheckFinding[];
  hasStaticCheckFindings: boolean;
  everTriggeredStaticCheckRules: {
    doubleQuote: boolean;
    brackets: boolean;
    all: boolean;
  };
  allSuggestionsForEditor: Array<SuggestionWithResolution | StaticCheckFinding>;
}

export type AnytimeConsultationCorrectionsGetterContext = AnytimeConsultationCorrectionsGetterValues;
export interface AnytimeConsultationCorrectionsGetters {
  suggestionsWithResolution(
    state: AnytimeConsultationCorrectionsState,
    getters: AnytimeConsultationCorrectionsGetterContext,
    rootState: RootStateWithAnswer
  ): SuggestionWithResolution[];
  newSuggestions(
    state: AnytimeConsultationCorrectionsState,
    getters: AnytimeConsultationCorrectionsGetterContext,
    rootState: RootStateWithAnswer
  ): SuggestionWithResolution[];
  newSuggestionsWithAnswerQuote(
    state: AnytimeConsultationCorrectionsState,
    getters: AnytimeConsultationCorrectionsGetterContext,
    rootState: RootStateWithAnswer
  ): SuggestionWithResolution[];
  groupedStatusCounts(
    state: AnytimeConsultationCorrectionsState,
    getters: AnytimeConsultationCorrectionsGetterContext,
    rootState: RootStateWithAnswer
  ): SuggestionResolutionCounts;
  correctionStatusesByType(
    state: AnytimeConsultationCorrectionsState,
    getters: AnytimeConsultationCorrectionsGetterContext,
    rootState: RootStateWithAnswer
  ): Record<CorrectionType, CorrectionStatus>;
  selectedSuggestionId(
    state: AnytimeConsultationCorrectionsState
  ): number | undefined;
  selectedUnansweredQuestionId(
    state: AnytimeConsultationCorrectionsState
  ): number | undefined;
  isAllCorrectionsResolved(
    state: AnytimeConsultationCorrectionsState,
    getters: AnytimeConsultationCorrectionsGetterContext,
    rootState: RootStateWithAnswer
  ): boolean;
  hasNotRequestedCorrections(
    state: AnytimeConsultationCorrectionsState,
    getters: AnytimeConsultationCorrectionsGetterContext,
    rootState: RootStateWithAnswer
  ): boolean;
  hasInProgressCorrections(
    state: AnytimeConsultationCorrectionsState,
    getters: AnytimeConsultationCorrectionsGetterContext,
    rootState: RootStateWithAnswer
  ): boolean;
  hasFailedCorrections(
    state: AnytimeConsultationCorrectionsState,
    getters: AnytimeConsultationCorrectionsGetterContext,
    rootState: RootStateWithAnswer
  ): boolean;
  statusEntries(
    state: AnytimeConsultationCorrectionsState,
    getters: AnytimeConsultationCorrectionsGetterContext,
    rootState: RootStateWithAnswer
  ): StatusEntry[];
  staticCheckFindings(
    state: AnytimeConsultationCorrectionsState,
    getters: AnytimeConsultationCorrectionsGetterContext,
    rootState: RootStateWithAnswer
  ): StaticCheckFinding[];
  hasStaticCheckFindings(
    state: AnytimeConsultationCorrectionsState,
    getters: AnytimeConsultationCorrectionsGetterContext,
    rootState: RootStateWithAnswer
  ): boolean;
  everTriggeredStaticCheckRules(
    state: AnytimeConsultationCorrectionsState,
    getters: AnytimeConsultationCorrectionsGetterContext,
    rootState: RootStateWithAnswer
  ): {
    doubleQuote: boolean;
      brackets: boolean;
      all: boolean;
  };
  allSuggestionsForEditor(
    state: AnytimeConsultationCorrectionsState,
    getters: AnytimeConsultationCorrectionsGetterContext,
    rootState: RootStateWithAnswer
  ): Array<SuggestionWithResolution | StaticCheckFinding>;
}

export type AnytimeConsultationCorrectionsMutations = Record<
  string,
  (state: AnytimeConsultationCorrectionsState, payload?: any) => void
>;

export interface AnytimeConsultationCorrectionsActions {
  initializeCorrections(context: StoreContext, consultationId: number): Promise<void>;
  requestCorrections(context: StoreContext, params: RequestCorrectionsParams): Promise<void>;
  acceptSuggestion(context: StoreContext, suggestionId: number): Promise<void>;
  ignoreSuggestion(context: StoreContext, suggestionId: number): Promise<void>;
  ignoreStaticCheckFinding(context: StoreContext, findingId: number): void;
  acceptStaticCheckFinding(context: StoreContext, findingId: number): void;
  updateEverTriggeredStaticCheckRules(context: StoreContext): void;
  fetchCorrectionsData(context: StoreContext, params: CorrectionsPollingParams): Promise<void>;
  selectSuggestionId(context: StoreContext, suggestionId: number): void;
  unsubscribeAnytimeConsultationChannel(context: StoreContext): void;
  resetCorrectionData(context: StoreContext): void;
}

export interface AnytimeConsultationCorrectionsStore {
  state: AnytimeConsultationCorrectionsState;
  getters: AnytimeConsultationCorrectionsGetters;
  actions: AnytimeConsultationCorrectionsActions;
  mutations: AnytimeConsultationCorrectionsMutations;
}

// -------------
// Storeの実装
// -------------
const initialState = (): AnytimeConsultationCorrectionsState => ({
  statuses: initialLlmServerStatuses(),
  suggestions: [],
  selectedSuggestionId: undefined,
  autoCorrectionChannel: null,
  hasConnectedOnce: false,
  thinkingProgress: initialThinkingProgress(),
  ignoredStaticCheckQuotes: [],
  everTriggeredStaticCheckRules: {
    doubleQuote: false,
    brackets: false,
    all: false,
  },
});

const state: AnytimeConsultationCorrectionsState = initialState();

type RootStateWithAnswer = {
  anytimeConsultations?: {
    anytimeConsultationAnswer?: { content?: string | null };
  };
  consultationPreprocessingRequest?: ConsultationPreprocessingRequestState;
};

const selectAnswerContent = (rootState: RootStateWithAnswer): string =>
  rootState?.anytimeConsultations?.anytimeConsultationAnswer?.content ?? "";

const countRegexMatches = (content: string, regex: RegExp): number => {
  return [...content.matchAll(regex)].length;
};

const selectExtractedQuestionsMap = (
  rootState: RootStateWithAnswer
): Record<number, ConsultationExtractedQuestionItem> => {
  const questions = rootState.consultationPreprocessingRequest?.questions ?? [];
  return questions.reduce<Record<number, ConsultationExtractedQuestionItem>>(
    (acc: Record<number, ConsultationExtractedQuestionItem>, question: ConsultationExtractedQuestionItem) => {
      acc[question.id] = question;
      return acc;
    },
    {}
  );
};

const getters: Record<string, unknown> & AnytimeConsultationCorrectionsGetters = {
  suggestionsWithResolution: (state, _getters, rootState: RootStateWithAnswer) => {
    const answerContent = selectAnswerContent(rootState);
    const extractedQuestionsMap = selectExtractedQuestionsMap(rootState);
    return buildSuggestionsWithResolution({
      suggestions: state.suggestions,
      answerContent,
      extractedQuestionsMap,
    });
  },
  newSuggestions: (_state, getters) => {
    return getters.suggestionsWithResolution.filter((suggestion) => suggestion.resolution === "new");
  },
  newSuggestionsWithAnswerQuote: (_state, getters) => { // Editorで使用するためのgetter。Editorでハイライトすべき回答文の引用が存在するsuggestionのみ返す
    return getters.newSuggestions.filter((suggestion) => !!suggestion.quote);
  },
  groupedStatusCounts: (_state, getters) => {
    return countSuggestionResolutions(getters.suggestionsWithResolution);
  },
  // correctionStatusesByType: 添削メニューごとのステータスを算出する
  // 出力例：
  // {
  //   typo: "suggestionPending",
  //   misreading: "suggestionResolved",
  //   unansweredQuestion: "noSuggestion",
  // }
  correctionStatusesByType: (state, getters) => {
    return correctionStatusesByType({
      statuses: state.statuses,
      suggestions: state.suggestions,
      resolvedSuggestions: getters.suggestionsWithResolution,
    });
  },
  selectedSuggestionId: (state) => state.selectedSuggestionId,
  selectedUnansweredQuestionId: (state) => {
    if (!state.selectedSuggestionId) return undefined;
    const suggestion = state.suggestions.find((item) => item.id === state.selectedSuggestionId);
    if (suggestion?.correctionType === "unansweredQuestion") {
      return suggestion.consultationExtractedQuestionId ?? undefined;
    }
    return undefined;
  },
  isAllCorrectionsResolved: (_state, getters) => {
    if (getters.hasStaticCheckFindings) return false;
    const statusesByType = getters.correctionStatusesByType;
    return CORRECTION_TYPES.every((type) => {
      const status = statusesByType[type];
      return status === "suggestionResolved" || status === "noSuggestion";
    });
  },
  hasNotRequestedCorrections: (_state, getters) => {
    const statusesByType = getters.correctionStatusesByType;
    return CORRECTION_TYPES.some((type) => statusesByType[type] === "notRequested");
  },
  hasInProgressCorrections: (_state, getters) => {
    const statusesByType = getters.correctionStatusesByType;
    return CORRECTION_TYPES.some((type) => isInProgressStatus(statusesByType[type]));
  },
  hasFailedCorrections: (_state, getters) => {
    const statusesByType = getters.correctionStatusesByType;
    return CORRECTION_TYPES.some((type) => statusesByType[type] === "failed");
  },
  statusEntries: (state, getters, rootState) => {
    const hasExtractedQuestions = getters.hasExtractedQuestions ?? false; // store consultation_preprocessing_requestのgetters
    const entries = getStatusEntries(getters.correctionStatusesByType, state.thinkingProgress, hasExtractedQuestions);
    if (getters.hasStaticCheckFindings || state.everTriggeredStaticCheckRules.all) {
      entries.push({
        type: "staticCheck" as const,
        label: "AI作成が疑われる表現",
        status: getters.hasStaticCheckFindings ? "suggestionPending" : "suggestionResolved",
        thinking: "",
        disabled: false,
      });
    }
    return entries;
  },
  staticCheckFindings: (state, _getters, rootState: RootStateWithAnswer) => {
    const answerContent = selectAnswerContent(rootState);
    const all = runStaticCheckRules(answerContent, {
      everTriggeredStaticCheckRules: state.everTriggeredStaticCheckRules,
    });
    const ignored = state.ignoredStaticCheckQuotes;
    if (ignored.length === 0) return all;
    // quote単位で無視する仕様。重複する同一quoteは一括で非表示にする。
    return all.filter((f) => !ignored.includes(f.quote));
  },
  hasStaticCheckFindings: (_state, getters) => {
    return getters.staticCheckFindings.length > 0;
  },
  everTriggeredStaticCheckRules: (state) => {
    return state.everTriggeredStaticCheckRules;
  },
  allSuggestionsForEditor: (_state, getters) => {
    return [
      ...getters.newSuggestionsWithAnswerQuote,
      ...getters.staticCheckFindings,
    ];
  },
};

const mutations: Record<string, unknown> & AnytimeConsultationCorrectionsMutations = {
  [mutationTypes.ASSIGN_ANYTIME_CONSULTATION_CORRECTIONS_STATUSES](
    state: AnytimeConsultationCorrectionsState,
    statuses: Partial<Record<CorrectionType, LlmServerStatus>>
  ) {
    state.statuses = { ...state.statuses, ...statuses };
  },
  [mutationTypes.ASSIGN_ANYTIME_CONSULTATION_CORRECTIONS_STATUS](
    state: AnytimeConsultationCorrectionsState,
    { correctionType, status }: { correctionType: CorrectionType; status: LlmServerStatus }
  ) {
    state.statuses = { ...state.statuses, [correctionType]: status } as LlmServerStatuses;
  },
  [mutationTypes.ASSIGN_ANYTIME_CONSULTATION_CORRECTIONS_SUGGESTIONS](
    state: AnytimeConsultationCorrectionsState,
    suggestions: Suggestion[]
  ) {
    state.suggestions = [...suggestions];
  },
  [mutationTypes.UPSERT_ANYTIME_CONSULTATION_CORRECTIONS_SUGGESTION](
    state: AnytimeConsultationCorrectionsState,
    suggestion: Suggestion
  ) {
    const index = state.suggestions.findIndex((item) => item.id === suggestion.id);
    if (index >= 0) {
      state.suggestions.splice(index, 1, suggestion);
    } else {
      state.suggestions.push(suggestion);
    }
  },
  [mutationTypes.ASSIGN_ANYTIME_CONSULTATION_CORRECTIONS_SELECTED_SUGGESTION_ID](
    state: AnytimeConsultationCorrectionsState,
    id: number | undefined
  ) {
    state.selectedSuggestionId = id;
  },
  [mutationTypes.ASSIGN_ANYTIME_CONSULTATION_CORRECTIONS_CHANNEL](
    state: AnytimeConsultationCorrectionsState,
    channel: Subscription | null
  ) {
    state.autoCorrectionChannel = channel ?? null;
  },
  [mutationTypes.ASSIGN_ANYTIME_CONSULTATION_CORRECTIONS_HAS_CONNECTED_ONCE](
    state: AnytimeConsultationCorrectionsState,
    hasConnected: boolean
  ) {
    state.hasConnectedOnce = hasConnected;
  },
  [mutationTypes.ASSIGN_ANYTIME_CONSULTATION_CORRECTIONS_THINKING_PROGRESS](
    state: AnytimeConsultationCorrectionsState,
    { correctionType, thinking }: { correctionType: CorrectionType; thinking: string }
  ) {
    state.thinkingProgress = {
      ...state.thinkingProgress,
      [correctionType]: thinking ?? "",
    } as ThinkingProgressMap;
  },
  [mutationTypes.CLEAR_ANYTIME_CONSULTATION_CORRECTIONS_THINKING_PROGRESS](
    state: AnytimeConsultationCorrectionsState,
    correctionTypes: CorrectionType[]
  ) {
    const next = { ...state.thinkingProgress } as ThinkingProgressMap;
    correctionTypes.forEach((type) => {
      next[type] = "";
    });
    state.thinkingProgress = next;
  },
  [mutationTypes.RESET_ANYTIME_CONSULTATION_CORRECTIONS_STATE](
    state: AnytimeConsultationCorrectionsState
  ) {
    Object.assign(state, initialState());
  },
  [mutationTypes.ADD_IGNORED_STATIC_CHECK_QUOTE](
    state: AnytimeConsultationCorrectionsState,
    quote: string
  ) {
    if (!state.ignoredStaticCheckQuotes.includes(quote)) {
      state.ignoredStaticCheckQuotes.push(quote);
    }
  },
  [mutationTypes.MARK_EVER_TRIGGERED_STATIC_CHECK_RULES](
    state: AnytimeConsultationCorrectionsState,
    payload: Partial<AnytimeConsultationCorrectionsState["everTriggeredStaticCheckRules"]>
  ) {
    const current = state.everTriggeredStaticCheckRules;
    const next = {
      ...current,
      ...payload,
    };
    if (
      current.doubleQuote === next.doubleQuote
      && current.brackets === next.brackets
      && current.all === next.all
    ) {
      return;
    }
    state.everTriggeredStaticCheckRules = next;
  },
};

const actions: Record<string, unknown> & AnytimeConsultationCorrectionsActions = {
  async initializeCorrections({ commit, dispatch }, consultationId: number) {
    await dispatch("fetchCorrectionsData", { consultationId });
    commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_CORRECTIONS_SELECTED_SUGGESTION_ID, undefined);
  },
  async requestCorrections(context, params: RequestCorrectionsParams) {
    await requestCorrectionsImpl(context, params);
  },
  async acceptSuggestion(context, payload: SuggestionCommandPayload | number) {
    const suggestionId = extractSuggestionId(payload);
    if (suggestionId == null) return;
    await acceptSuggestion(context, suggestionId);
    context.commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_CORRECTIONS_SELECTED_SUGGESTION_ID, undefined);
  },
  async ignoreSuggestion(context, payload: SuggestionCommandPayload | number) {
    const suggestionId = extractSuggestionId(payload);
    if (suggestionId == null) return;
    await ignoreSuggestion(context, suggestionId);
    context.commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_CORRECTIONS_SELECTED_SUGGESTION_ID, undefined);
  },
  ignoreStaticCheckFinding({ commit, getters }, findingId: number) {
    const finding = getters.staticCheckFindings.find((f: StaticCheckFinding) => f.id === findingId);
    if (!finding) return;
    commit(mutationTypes.ADD_IGNORED_STATIC_CHECK_QUOTE, finding.quote);
    commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_CORRECTIONS_SELECTED_SUGGESTION_ID, undefined);
  },
  updateEverTriggeredStaticCheckRules({ commit, rootState }) {
    const answerContent = selectAnswerContent(rootState as RootStateWithAnswer);
    const currentFindings = runStaticCheckRules(answerContent, {
      everTriggeredStaticCheckRules: {
        doubleQuote: false,
        brackets: false,
      },
    });
    const doubleQuoteMatches = countRegexMatches(answerContent, /\u201c[^\u201d\n]+\u201d|"[^"\n]+"/g);
    const bracketsMatches = countRegexMatches(answerContent, /「[^」\n]+」/g);

    const payload: Partial<AnytimeConsultationCorrectionsState["everTriggeredStaticCheckRules"]> = {};
    if (doubleQuoteMatches >= 2) payload.doubleQuote = true;
    if (bracketsMatches >= 2) payload.brackets = true;
    if (currentFindings.length > 0) payload.all = true;

    if (Object.keys(payload).length > 0) {
      commit(mutationTypes.MARK_EVER_TRIGGERED_STATIC_CHECK_RULES, payload);
    }
  },
  acceptStaticCheckFinding({ commit, getters, rootState }: StoreContext, findingId: number) {
    const finding = getters.staticCheckFindings.find((f: StaticCheckFinding) => f.id === findingId);
    if (!finding || finding.replacement == null) return;
    const answer = (rootState as RootStateWithAnswer).anytimeConsultations?.anytimeConsultationAnswer;
    if (answer) {
      // NOTE:
      // staticCheckの「問題ありません / 置換」は quote単位の一括処理を意図している。
      // 同じquoteが複数箇所にある場合でも、選択した1件だけではなく同一quoteをまとめて置換する。
      const content = replaceAllOccurrences(answer.content ?? "", finding.quote, finding.replacement);
      commit(mutationTypes.SET_ANYTIME_CONSULTATION_ANSWER_CONTENT, { content });
    }
    commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_CORRECTIONS_SELECTED_SUGGESTION_ID, undefined);
  },
  async fetchCorrectionsData({ commit, dispatch, getters }, { consultationId }: CorrectionsPollingParams) {
    try {
      const { statuses, suggestions } = await fetchInitialCorrectionsData(consultationId);
      commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_CORRECTIONS_STATUSES, statuses);
      commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_CORRECTIONS_SUGGESTIONS, suggestions);
    } catch (error) {
      console.warn("Failed to fetch consultation corrections", error);
      return;
    }

    if (!getters.hasInProgressCorrections) {
      dispatch("unsubscribeAnytimeConsultationChannel");
      return;
    }
  },
  selectSuggestionId({ commit }, suggestionId) {
    commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_CORRECTIONS_SELECTED_SUGGESTION_ID, suggestionId);
  },
  unsubscribeAnytimeConsultationChannel({ commit, state }) {
    if (state.autoCorrectionChannel) {
      state.autoCorrectionChannel.unsubscribe();
      commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_CORRECTIONS_CHANNEL, null);
    }
    commit(mutationTypes.ASSIGN_ANYTIME_CONSULTATION_CORRECTIONS_HAS_CONNECTED_ONCE, false);
  },
  resetCorrectionData({ commit, dispatch }) {
    dispatch("unsubscribeAnytimeConsultationChannel");
    commit(mutationTypes.RESET_ANYTIME_CONSULTATION_CORRECTIONS_STATE);
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
} as AnytimeConsultationCorrectionsStore;
